/* ============================================================
   ELLIANOS ENERGY — NEON-WEDGE BACKGROUND
   Soft faded burgundy / orange / blue / pink wedges in the top-right and
   bottom-left corners that slide in diagonally and crossfade through the brand
   palette, each with a glowing neon edge, over a white field.

   Extracted from the inline <script> that used to live in
   snippets/slideshow-energy-bg.liquid. The drawing code is unchanged; the
   scaffolding around it was rebuilt to match assets/slideshow-pumpkin-bg.js so the
   two can coexist as cross-fading layers:
     · per-canvas instances instead of "the last .ee-bg-canvas on the page"
     · idempotent init that also runs on shopify:section:load (theme editor)
     · a zero-size guard in measure()
     · pause/resume through the shared layer gate (see gate() below)
     · an elapsed-time accumulator rather than an absolute clock, so resuming
       after a pause continues the wedge crossfade instead of jumping mid-transition
   Rendered by snippets/slideshow-energy-bg.liquid.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- tuning ---------- */
  var CONFIG = {
    COLORS: ['#6B1229', '#E08A3C', '#2E7DD6', '#D4537E'], // burgundy, orange, blue, pink
    // Brighter neon edge colour mapped to each fill colour:
    NEON: {
      '#6B1229': '#FF2D6B', // burgundy -> hot pink
      '#E08A3C': '#FFB43C', // orange   -> amber gold
      '#2E7DD6': '#3CC8FF', // blue     -> electric cyan
      '#D4537E': '#FF6BC8' // pink     -> bright pink
    },
    HOLD: 2800, // ms each colour stays fully in
    TRANS: 1500, // ms smooth diagonal slide + crossfade transition
    MAXA: 0.16, // wedge fill opacity (faded)
    REACH: 0.46, // wedge horizontal span (fraction of width)
    DROP: 0.72, // wedge vertical extent (fraction of height)
    FEATHER: 0.42, // fraction of wedge that fades to transparent (soft inner edge)
    OVER: 90, // px overshoot past edges so corners fill solidly
    PULSE: 0.06, // gentle fill opacity breath while holding (0 = off)
    GLOW: 0.55 // neon edge overall strength (0 = off, 1 = full)
  };

  var CYCLE = CONFIG.HOLD + CONFIG.TRANS;

  function ease(x) {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  }
  function colorAt(i) {
    var n = CONFIG.COLORS.length;
    return CONFIG.COLORS[((Math.floor(i) % n) + n) % n];
  }

  /* ---------- pause/resume contract, shared with slideshow-pumpkin-bg.js ----------
     A background may be rendered inside a layer carrying data-bg-layer="<key>" and
     data-bg-state="active|inactive" (snippets/slideshow-backgrounds.liquid). The RAF
     loop runs only when the layer is not inactive, the canvas is on screen, and the
     tab is visible. The current attribute is read at init and observed for changes,
     so this is immune to the order the deferred scripts happen to run in. With no
     [data-bg-layer] ancestor the background is simply always active. */
  function gate(canvas, start, stop) {
    var layer = canvas.closest ? canvas.closest('[data-bg-layer]') : null;
    var onScreen = true;
    var active = true;

    function sync() {
      if (active && onScreen && !document.hidden) start();
      else stop();
    }

    if (layer) {
      active = layer.getAttribute('data-bg-state') !== 'inactive';
      if (window.MutationObserver) {
        new MutationObserver(function () {
          active = layer.getAttribute('data-bg-state') !== 'inactive';
          sync();
        }).observe(layer, { attributes: true, attributeFilter: ['data-bg-state'] });
      }
    }

    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[entries.length - 1].isIntersecting;
        sync();
      }).observe(canvas);
    }

    document.addEventListener('visibilitychange', sync);
    sync();
    return sync;
  }

  /* ---------- one background instance ---------- */
  function EnergyField(canvas) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return false;

    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var W = 0,
      H = 0,
      dpr = 1,
      raf = null,
      last = 0,
      elapsed = 0,
      sync = function () {}; // replaced by gate() below; a no-op until then

    function measure() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      var r = canvas.getBoundingClientRect();
      W = r.width;
      H = r.height;
      if (!W || !H) return false;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return true;
    }

    function base() {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, W, H);
    }

    // Glowing neon line from (x1,y1) to (x2,y2), tinted to match the fill colour.
    function neonEdge(x1, y1, x2, y2, color, alpha, glow) {
      if (glow <= 0 || alpha <= 0) return;
      var nc = CONFIG.NEON[color] || color;
      ctx.save();
      ctx.lineCap = 'round';
      ctx.globalAlpha = Math.min(1, alpha * 0.22 * glow);
      ctx.strokeStyle = nc;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.globalAlpha = Math.min(1, alpha * 0.6 * glow);
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.restore();
    }

    // Top-right wedge: slides in diagonally from the top-left.
    function topRight(color, p, alpha, glow) {
      if (alpha <= 0 || !color) return;
      var reach = W * CONFIG.REACH,
        drop = H * CONFIG.DROP;
      var off = 1 - p,
        ox = -off * (reach + CONFIG.OVER),
        oy = -off * (drop + CONFIG.OVER);
      var innerX = W - reach;
      var g = ctx.createLinearGradient(innerX + ox, 0, W + CONFIG.OVER + ox, 0);
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(CONFIG.FEATHER, color);
      g.addColorStop(1, color);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(innerX + ox, 0 + oy);
      ctx.lineTo(W + CONFIG.OVER + ox, 0 + oy);
      ctx.lineTo(W + CONFIG.OVER + ox, drop + oy);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      neonEdge(
        innerX + ox,
        0 + oy,
        W + CONFIG.OVER + ox,
        drop + oy,
        color,
        (alpha / CONFIG.MAXA) * 0.9,
        glow * CONFIG.GLOW
      );
    }

    // Bottom-left wedge: slides in diagonally from the bottom-right.
    function bottomLeft(color, p, alpha, glow) {
      if (alpha <= 0 || !color) return;
      var reach = W * CONFIG.REACH,
        rise = H * CONFIG.DROP;
      var off = 1 - p,
        ox = off * (reach + CONFIG.OVER),
        oy = off * (rise + CONFIG.OVER);
      var innerX = reach;
      var g = ctx.createLinearGradient(innerX + ox, 0, -CONFIG.OVER + ox, 0);
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(CONFIG.FEATHER, color);
      g.addColorStop(1, color);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(innerX + ox, H + oy);
      ctx.lineTo(-CONFIG.OVER + ox, H + oy);
      ctx.lineTo(-CONFIG.OVER + ox, H - rise + oy);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      neonEdge(
        innerX + ox,
        H + oy,
        -CONFIG.OVER + ox,
        H - rise + oy,
        color,
        (alpha / CONFIG.MAXA) * 0.9,
        glow * CONFIG.GLOW
      );
    }

    function draw(t) {
      var step = Math.floor(t / CYCLE);
      var cur = colorAt(step);
      var nxt = colorAt(step + 1);
      var inCycle = t % CYCLE;

      base();
      var pulse = 1 + Math.sin(t * 0.0018) * CONFIG.PULSE;
      var glowPulse = 0.8 + Math.sin(t * 0.004) * 0.2;

      if (inCycle < CONFIG.HOLD) {
        topRight(cur, 1, CONFIG.MAXA * pulse, glowPulse);
        bottomLeft(cur, 1, CONFIG.MAXA * pulse, glowPulse);
      } else {
        var e = ease((inCycle - CONFIG.HOLD) / CONFIG.TRANS);
        topRight(cur, 1, CONFIG.MAXA * (1 - e), glowPulse * (1 - e));
        bottomLeft(cur, 1, CONFIG.MAXA * (1 - e), glowPulse * (1 - e));
        topRight(nxt, e, CONFIG.MAXA * e, glowPulse * e);
        bottomLeft(nxt, e, CONFIG.MAXA * e, glowPulse * e);
      }
    }

    function loop(t) {
      if (!last) last = t;
      // accumulate, so a pause does not jump the crossfade
      elapsed += Math.min(t - last, 50);
      last = t;
      draw(elapsed);
      raf = requestAnimationFrame(loop);
    }

    function start() {
      if (raf || reduceMotion) return;
      last = 0;
      raf = requestAnimationFrame(loop);
    }

    function stop() {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      last = 0;
    }

    function resize() {
      if (!measure()) return;
      draw(elapsed); // keep a painted frame, even while paused
    }

    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        stop();
        resize();
        sync();
      }).observe(canvas);
    } else {
      window.addEventListener(
        'resize',
        function () {
          stop();
          resize();
          sync();
        },
        { passive: true }
      );
    }

    resize(); // one static frame at init, so a layer is never blank when it fades in
    // Under reduced motion start() is a no-op and .ee-bg-static covers the layer,
    // so the gate is still wired up — it simply never starts a loop.
    sync = gate(canvas, start, stop);
    return true;
  }

  /* ---------- bootstrap: idempotent, and re-runs for the theme editor ---------- */
  function init() {
    var canvases = document.querySelectorAll('.ee-bg-canvas');
    for (var i = 0; i < canvases.length; i++) {
      var cv = canvases[i];
      if (cv.dataset.energyBgReady === 'true') continue;
      cv.dataset.energyBgReady = 'true';
      EnergyField(cv);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
